/* Copyright (c) 2020-2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Container } from "react-awesome-styled-grid";
import { useParams } from "react-router-dom";
import { Breadcrumbs } from "~/components";
import { AssignmentRuleDelete } from "~/components/AssignmentRules/AssignmentRuleDelete";
import { AssignmentRuleDetailsInfo } from "~/components/AssignmentRules/AssignmentRuleDetailsInfo";
import { AssignmentRuleDetailsServices } from "~/components/AssignmentRules/AssignmentRuleDetailsServices";
import { TabsPath } from "~/components/Customer/CustomerNavigation";
import { useShowError } from "~/components/Error/useShowError";
import { LoaderWrap } from "~/components/LoaderWrap/LoaderWrap";
import { useCustomer } from "~/context/User/Customer";
import { useUserConsumer } from "~/context/User/User";
import { ARAttachedServices, AssignmentRule } from "~/models/assignmentRule";
import { ACTIONS, Can, isStandardUser } from "~/routing";
import { useAxios } from "~/tools";
import { AssignmentRuleInitialState } from "../initialState";

type RouteParams = {
  toolId: string;
  assignmentRuleId: string;
  customerId: string;
};

function AssignmentRuleDetails() {
  const state = useParams<RouteParams>();
  const { customerId, assignmentRuleId } = state;
  const { customer } = useCustomer();

  const [assignmentRule, setAssignmentRule] = React.useState<AssignmentRule>(
    AssignmentRuleInitialState
  );
  const [isLoading, setIsLoading] = React.useState(true);

  const { showError } = useShowError();
  const { Axios } = useAxios();

  const { role } = useUserConsumer();
  const canEdit = isStandardUser(role);

  const fetchAssignmentRule = React.useCallback(async () => {
    try {
      const { data } = await Axios().get(
        `/customers/${customerId}/assignmentRules/${assignmentRuleId}`
      );
      setAssignmentRule(data);
    } catch (e) {
      showError(e.response);
    } finally {
      setIsLoading(false);
    }
  }, [Axios, assignmentRuleId, customerId, showError]);

  React.useEffect(() => {
    fetchAssignmentRule();
  }, [fetchAssignmentRule]);

  const breadcrumbLabels = [
    { label: "Customers", uri: "/" },
    {
      uri: `/customers/${customerId}/${TabsPath.INFO}`,
      label: customer.name,
    },
    {
      uri: `/customers/${customerId}/${TabsPath.ASSIGNMENT_RULES}`,
      label: "Assignment Rules",
    },
    { uri: assignmentRule.id, label: assignmentRule?.name },
  ];

  const { attachedServices } = assignmentRule;

  return (
    <Container>
      {assignmentRule.id?.length !== 0 && (
        <Breadcrumbs
          title={assignmentRule?.name || ""}
          customBreadcrumbs={breadcrumbLabels}
        />
      )}

      <LoaderWrap loading={isLoading}>
        <AssignmentRuleDetailsInfo
          assignmentRule={assignmentRule}
          refetch={fetchAssignmentRule}
          canEdit={canEdit}
        />
      </LoaderWrap>
      <AssignmentRuleDetailsServices
        fetchAssignmentRule={fetchAssignmentRule}
        attachedServices={attachedServices as ARAttachedServices[]}
        canEdit={canEdit}
      />
      <Can action={ACTIONS.DELETE_CUSTOMER_ASSIGNMENT}>
        <AssignmentRuleDelete assignmentRule={assignmentRule} />
      </Can>
    </Container>
  );
}

export { AssignmentRuleDetails };
